// MobilePublicSchedule — phone view for the 3v3 + 1v1 schedule pages.
//
// Renders a flat list of MobileMatchRow rows (no NOW / Up Next cards
// — live state is signalled by the row's LIVE chip and yellow accent).
// Reuses the desktop helpers from src/state/schedule.ts (dayKey,
// breakBetween, displayTimeFor) so the multi-day grouping + delay
// behaviour matches what spectators see on desktop.
//
// State (currently nothing — the only filter is the event tab, which is
// the route itself) is owned by the parent ProtoPublicSchedule wrapper,
// so resizing back to desktop preserves whichever event the user was on.

import { Fragment, useMemo } from 'react';
import type { Dispatch } from 'react';
import type { Action, AppRole, AppState, GroupLetter, Match, NavigateFn } from '../../types';
import {
  DEFAULT_ARC_SCHEDULE, breakBetween, dayKey, displayTimeFor, formatScheduledAt,
  formatScheduledDay, groupOfMatch, groupsInMatches, resolveBlocks,
} from '../../state/schedule';
import { ProtoClockChip, GroupFilter } from '../../components';
import { MobileTopbar } from '../../components/MobileTopbar';
import { MobileMatchRow } from '../../components/MobileMatchRow';
import { TEAMS } from '../../mock/data';
import { totalSwissMatches } from '../../state/swiss';

const COMPLETED_LIKE: Match['redS'][] = ['completed', 'forfeit_v', 'forfeit_iv'];
const isLiveOrUpcoming = (m: Match) =>
  m.live || m.onDeck || !(COMPLETED_LIKE.includes(m.redS) && COMPLETED_LIKE.includes(m.blueS));

export function MobilePublicSchedule({
  event, state, onNavigate, onMenu, role, dispatch, groupFilter = 'all', setGroupFilter = () => {},
}: {
  event: '3v3' | '1v1';
  state: AppState;
  onNavigate?: NavigateFn;
  onMenu: () => void;
  /** Forwarded to MobileMatchRow so ops can tap a status pill to edit it.
   *  Without role/dispatch the pills stay read-only (public default). */
  role?: AppRole;
  dispatch?: Dispatch<Action>;
  /** 3v3 group filter, owned + persisted by the ProtoPublicSchedule wrapper so
   *  the choice survives a mobile⇄desktop resize. Defaults to unfiltered when
   *  the component is rendered standalone (tests). */
  groupFilter?: GroupLetter | 'all';
  setGroupFilter?: (g: GroupLetter | 'all') => void;
}) {
  // 3v3 group filter (mirrors the desktop schedule). `all` stays UNFILTERED so
  // renderTime keeps the full schedule for block-relative delays; only the
  // displayed rows + counts narrow via inActiveGroup.
  const activeGroup = event === '3v3' ? groupFilter : 'all';
  const availableGroups = event === '3v3' ? groupsInMatches(state.matches3v3, state.teamConfig) : [];
  const showGroupFilter = availableGroups.length > 1;
  const inActiveGroup = (m: Match) => activeGroup === 'all' || groupOfMatch(m, state.teamConfig) === activeGroup;

  const all = event === '3v3' ? state.matches3v3 : state.matches1v1;
  // Byes are auto-completed (dropped by isLiveOrUpcoming), but a byed team should
  // still see their bye — surface current/upcoming-round byes (at/after the
  // earliest still-to-play match) and sort so each slots in beside its round.
  // Mirrors the desktop schedule. MobileMatchRow renders the bye row itself.
  const liveOrUpcoming = all.filter(isLiveOrUpcoming).filter(inActiveGroup);
  const byeFrontier = liveOrUpcoming.map((m) => m.scheduledAt).filter((s): s is string => !!s).sort()[0];
  const matches = [
    ...liveOrUpcoming,
    ...all.filter((m) => m.bye && inActiveGroup(m) && m.scheduledAt && byeFrontier !== undefined && m.scheduledAt >= byeFrontier),
  ].sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
  // 1v1's later Swiss rounds aren't generated yet — derive the true remaining
  // count from the field size (byes excluded). Mirrors the desktop schedule.
  const concludedReal = all.filter((m) => !m.bye && !isLiveOrUpcoming(m)).length;
  const n1v1 = TEAMS.filter((t) => t.events.includes('1v1')).length;
  const upcomingCount = event === '1v1'
    ? Math.max(0, totalSwissMatches(n1v1) - concludedReal)
    : matches.length;
  const completedCount = event === '1v1'
    ? concludedReal
    : all.filter((m) => inActiveGroup(m) && !m.bye && !isLiveOrUpcoming(m)).length;

  const scheduleBlocks = useMemo(() => resolveBlocks(DEFAULT_ARC_SCHEDULE), []);
  const delay = state.officialDelay[event] ?? 0;
  const delayAnchor = state.officialDelayBlock[event] ?? null;
  const renderTime = (m: Match): { time: string; delayed: boolean } => {
    const r = displayTimeFor(m, all, scheduleBlocks, delay, delayAnchor);
    return {
      time: r.effective ? formatScheduledAt(r.effective) : m.time,
      delayed: r.delayed,
    };
  };

  const upcomingDays = Array.from(new Set(matches.map((m) => dayKey(m.scheduledAt)).filter(Boolean)));
  // Always show a day header so the date is visible even for a single-day list
  // (e.g. 1v1 before its later Swiss rounds exist); multi-day still gets one per day.
  const showDayMarkers = upcomingDays.length > 0;

  return (
    <div className="mw-app">
      <MobileTopbar
        title={`${event.toUpperCase()} SCHEDULE`}
        subtitle={`${upcomingCount} UPCOMING · ${completedCount} COMPLETED`}
        onMenu={onMenu}
        right={<ProtoClockChip delayMin={state.officialDelay[event] ?? 0} event={event} hideTime />}
      />
      <div className="mw-body">
        <div className="mw-list-head">
          <span>NEXT</span>
          <span>{completedCount} COMPLETED · SEE RESULTS</span>
        </div>
        {showGroupFilter && (
          <div className="row gap-2" style={{ padding: '8px 12px 0' }}>
            <span className="mono txt-10 text-3 tracking-2" style={{ alignSelf: 'center' }}>GROUP</span>
            <GroupFilter value={groupFilter} onChange={setGroupFilter} groups={availableGroups} />
          </div>
        )}
        {matches.length === 0 ? (
          <div className="mw-empty">ALL SCHEDULED MATCHES COMPLETE</div>
        ) : (() => {
          let lastDay = '';
          let prevMatch: Match | null = null;
          return matches.map((m) => {
            const today = dayKey(m.scheduledAt);
            const dayChanged = showDayMarkers && today && today !== lastDay;
            if (dayChanged) lastDay = today;
            const brk = !dayChanged ? breakBetween(prevMatch, m, scheduleBlocks) : null;
            prevMatch = m;
            const tdisp = renderTime(m);
            return (
              <Fragment key={m.id}>
                {dayChanged && (
                  <div className="mw-day-marker">{formatScheduledDay(m.scheduledAt).toUpperCase()}</div>
                )}
                {brk && (
                  <div
                    className="mw-day-marker"
                    style={{ background: 'transparent', borderTop: '1px dashed var(--line)', borderBottom: '1px dashed var(--line)', textAlign: 'center' }}
                  >
                    ── {brk.label.toUpperCase()} BREAK · {formatScheduledAt(brk.starts)} – {formatScheduledAt(brk.ends)} ──
                  </div>
                )}
                <MobileMatchRow
                  kind="schedule"
                  match={m}
                  event={event}
                  state={state}
                  timeText={tdisp.time}
                  timeDelayed={tdisp.delayed}
                  onNavigate={onNavigate}
                  role={role}
                  dispatch={dispatch}
                />
              </Fragment>
            );
          });
        })()}
        <div className="mw-tail-spacer" />
      </div>
    </div>
  );
}
